import type { LucideIcon } from 'lucide-react';
import {
  Sparkles,
  Archive,
  MonitorSmartphone,
  BellRing,
  HeartPulse,
  GraduationCap,
  Award,
  Handshake,
  PlayCircle,
  HardDrive,
  FileDown,
  BookOpen,
  Building,
  KeyRound,
} from 'lucide-react';
import { PLATFORM_MODULES } from '@/content/platform';
import { INDUSTRIES } from '@/content/industries';
import { HELP_URL, INK_U_URL, STATS } from '@/lib/site';

export interface MenuLink {
  label: string;
  to: string;
  description?: string;
  icon?: LucideIcon;
  color?: string;
}

export interface MenuFeature {
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  to: string;
}

export interface MegaMenu {
  label: string;
  /** Where the top-level label goes when clicked. */
  to: string;
  columns: Array<{ title: string; links: MenuLink[] }>;
  feature?: MenuFeature;
}

export const MEGA_MENUS: MegaMenu[] = [
  {
    label: 'Platform',
    to: '/platform',
    columns: [
      {
        title: 'Capabilities',
        links: PLATFORM_MODULES.slice(0, 5).map((m) => ({
          label: m.name,
          to: `/platform/${m.slug}`,
          description: m.summary,
          icon: m.icon,
          color: m.color,
        })),
      },
      {
        title: ' ',
        links: PLATFORM_MODULES.slice(5).map((m) => ({
          label: m.name,
          to: `/platform/${m.slug}`,
          description: m.summary,
          icon: m.icon,
          color: m.color,
        })),
      },
    ],
    feature: {
      eyebrow: 'Hardware',
      title: 'The Scada.Ink Appliance',
      body: 'Preconfigured, fanless, DIN-rail mounted — with I/O and serial built in.',
      cta: 'See the appliance',
      to: '/hardware',
    },
  },
  {
    label: 'Cloud',
    to: '/cloud',
    columns: [
      {
        title: 'Scada.Ink Cloud services',
        links: [
          { label: 'Smart Assist AI', to: '/cloud#ai', description: 'AI commissioning and page design', icon: Sparkles, color: '#8b5cf6' },
          { label: 'Cloud Data Archive', to: '/cloud#archive', description: 'Off-site history, up to 7 years', icon: Archive, color: '#16a34a' },
          { label: 'Remote Viewing', to: '/cloud#remote', description: 'Dashboards, pages & reports anywhere', icon: MonitorSmartphone, color: '#3b82f6' },
          { label: 'SMS & Voice Alerts', to: '/cloud#alerts', description: 'Twilio-powered alarm notifications', icon: BellRing, color: '#dc2626' },
          { label: 'Fleet Health', to: '/cloud#fleet', description: 'Appliance health from anywhere — free', icon: HeartPulse, color: '#06b6d4' },
        ],
      },
    ],
    feature: {
      eyebrow: 'Self-serve',
      title: 'Plans from $25 a month',
      body: 'Subscribe in the customer portal and manage everything from one place.',
      cta: 'Compare plans',
      to: '/pricing#cloud',
    },
  },
  {
    label: 'Industries',
    to: '/industries',
    columns: [
      {
        title: 'Industries',
        links: INDUSTRIES.slice(0, 4).map((i) => ({ label: i.name, to: `/industries#${i.slug}`, icon: i.icon, color: '#3b82f6' })),
      },
      {
        title: ' ',
        links: INDUSTRIES.slice(4).map((i) => ({ label: i.name, to: `/industries#${i.slug}`, icon: i.icon, color: '#7c3aed' })),
      },
    ],
  },
  {
    label: 'Training',
    to: '/training',
    columns: [
      {
        title: 'Ink U',
        links: [
          { label: 'Ink U Training Portal', to: '/training', description: 'Free video courses with quizzes', icon: GraduationCap, color: '#7c3aed' },
          { label: 'Certification Program', to: '/training/certification', description: 'Associate & Professional credentials', icon: Award, color: '#ca8a04' },
          { label: 'Course Catalog', to: '/training#courses', description: `${STATS.courses} courses, start to finish`, icon: PlayCircle, color: '#3b82f6' },
          { label: 'Partner Program', to: '/partners', description: 'Up to 50% off for certified partners', icon: Handshake, color: '#16a34a' },
        ],
      },
    ],
    feature: {
      eyebrow: 'Free',
      title: 'Start learning today',
      body: 'Every course, quiz and transcript on Ink U is free with registration.',
      cta: 'Open Ink U',
      to: INK_U_URL,
    },
  },
  {
    label: 'Resources',
    to: '/resources',
    columns: [
      {
        title: 'Resources',
        links: [
          { label: 'License Builder', to: '/license-builder', description: 'Pick features and see the price', icon: KeyRound, color: '#ca8a04' },
          { label: 'Downloads & Datasheets', to: '/resources', description: 'Overview and feature sheets', icon: FileDown, color: '#3b82f6' },
          { label: 'Hardware', to: '/hardware', description: 'Appliance specifications', icon: HardDrive, color: '#0f766e' },
          { label: 'Help Center', to: HELP_URL, description: 'Searchable documentation for every feature', icon: BookOpen, color: '#7c3aed' },
          { label: 'Company', to: '/company', description: 'Who we are and why', icon: Building, color: '#64748b' },
        ],
      },
    ],
  },
];

export const UTILITY_LINKS: MenuLink[] = [
  { label: 'Ink U Training', to: INK_U_URL },
  { label: 'Help Center', to: HELP_URL },
  { label: 'Partners', to: '/partners' },
  { label: 'Company', to: '/company' },
  { label: 'Contact', to: '/contact' },
];
